/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import documentAPI from 'api/documentsAPI';
import { Button, Container, Dropdown, LoadingScreen } from '@punchcard/core';
import { IconArrowLeft, IconChevronRight, IconDeselectMulti, IconPreparing, IconSelectBox, IconSelectBoxAll, IconSelectBoxIndeterminate, IconSelectMulti, IconWorkSample } from '@punchcard/core/icons';
import WorkSample from '@punchcard/core/components/WorkSample/WorkSample';
import Whiteboard from '@punchcard/core/components/WorkSample/Whiteboard';
import { FormProvider, useForm } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import WorkSampleExam from '@punchcard/core/components/exams/WorkSampleExam';
import { parseNumberSeries } from '@punchcard/core/utils/utils';

function WorkSamplePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { studentId, unitId, courseId } = useParams();
	const [loading, setLoading] = React.useState(false);
	const [selectedPages, setSelectedPages] = React.useState<number[]>([]);
	const [includeExam, setIncludeExam] = React.useState(false);
	const methods = useForm<StudentWorkSampleDTO>({
		defaultValues: {
			workSampleComment: '',
			studentId: parseInt(studentId ?? '0'),
			courseUnitId: parseInt(unitId ?? '0'),
			courseId: parseInt(courseId ?? '0'),
			documents: [],
		}
	});
	const workSample = methods.watch();
	const documentPageCount = workSample.documents.flatMap(x => x.pageNumbers ? parseNumberSeries(x.pageNumbers) : []).length;
	const selectedAllPages = documentPageCount === selectedPages.length && workSample.includeWhiteboard === true && includeExam === true;
	const showExamAnswers = workSample.examAnswers && workSample.examAnswers.length !== 0;
	const isPreparing = workSample.submittedDate !== undefined && workSample.pdfProcessedDate === undefined;
	const fetchSample = React.useCallback(async () => {
		try {
			setLoading(true);
			const sample = await documentAPI.getStudentWorkSample(studentId, unitId);
			methods.reset(sample);
			setIncludeExam(sample.includeExam);
			setSelectedPages(sample.selectedPages === '' ? [] : sample.selectedPages.split(',').map(num => parseInt(num.trim(), 10)));
		} catch (error) {
			console.error('Error fetching sample:', error);
		} finally {
			setLoading(false);
		}
	}, [methods, studentId, unitId]);
	React.useEffect(() => {
		fetchSample();
	}, [fetchSample]);
	const getSelectIcon = () => {
		const pageCount = workSample.documents
			.filter(x => x.pageNumbers)
			.flatMap(x => parseNumberSeries(x.pageNumbers))
			.filter((val, index, self) => self.indexOf(val) === index).length; // remove duplicates

		if (selectedPages.length === 0 && includeExam === false && workSample.includeWhiteboard === false) {
			return <IconSelectBox className="icon-22" />;
		} else if (selectedPages.length === pageCount && includeExam === true && workSample.includeWhiteboard === true) {
			return <IconSelectBoxAll className="icon-22" />;
		} else {
			return <IconSelectBoxIndeterminate className="icon-22" />;
		}
	};
	const handleGenerateWorkSample = React.useCallback(() => {
		const data = methods.getValues();
		data.selectedPages = selectedPages.join(',');
		data.includeExam = includeExam;
		if (data.submittedDate && !data.pdfProcessedDate) { return; }
		documentAPI.postStudentWorkSample(data).then(methods.reset);
	}, [includeExam, methods, selectedPages]);

	const selectAllPages = () => {
		const pages = workSample.documents
			.filter(x => x.pageNumbers)
			.flatMap(x => parseNumberSeries(x.pageNumbers))
			.filter((val, index, self) => self.indexOf(val) === index); // remove duplicates

		setSelectedPages(pages);
		methods.setValue('includeWhiteboard', true);
		setIncludeExam(true);
	};
	const selectGradedPages = () => {
		const pages = workSample.documents
			.filter(x => x.gradeValue !== undefined && x.pageNumbers)
			.flatMap(x => parseNumberSeries(x.pageNumbers))
			.filter((val, index, self) => self.indexOf(val) === index); // remove duplicates

		setSelectedPages(prevSelectedPages => {	// update the selected pages, but not removing the already selected pages
			const combinedPages = [...prevSelectedPages, ...pages];
			const uniquePages = Array.from(new Set(combinedPages));
			return uniquePages;
		});
		methods.setValue('includeWhiteboard', true);
		setIncludeExam(true);
	};

	const deSelectAllPages = () => {
		setSelectedPages([]);
		methods.setValue('includeWhiteboard', false);
		setIncludeExam(false);
	};
	const handleBackButton = React.useCallback(() => {
		navigate(`/students/${studentId}/course/${courseId}/unit/${unitId}`);
	}, [navigate, studentId, courseId, unitId]);
	if (loading) {
		return <LoadingScreen />;
	}

	return (
		<div className="d-flex flex-column flex-fill">
			<header className="px-3 py-2 d-flex align-items-center justify-content-between">
				<div className="d-flex align-items-center">
					<div className="pe-3 me-3 border-end">
						<Button className="btn-ghost-primary" icon={<IconArrowLeft />} onClick={handleBackButton}>{t('back')}</Button>
					</div>
					<h6>
						{workSample?.firstName} {workSample?.lastName}
						<IconChevronRight className="icon-27" />
						{workSample?.courseName ?? t('wip.course')}
						<IconChevronRight className="icon-27" />
						{workSample?.courseUnitName ?? t('wip.unit')}
						{' ' + t('wip.work_sample')}
					</h6>
				</div>
			</header>
			<div className="bg-light d-flex flex-column overflow-hidden justify-content-center m-auto rounded-2">
				<div className="d-flex align-items-center bg-secondary-100 justify-content-between" >
					{isPreparing && <Tooltip id={'tooltip-select'} place="top-start" />}
					{!includeExam && !workSample.includeWhiteboard && selectedPages?.length === 0 && <Tooltip id={'tooltip-generate'} place="top" />}
					<div className="d-flex w-100 align-items-center" style={{ zIndex: 2000 }}>
						<span
							data-tooltip-id={'tooltip-select'}
							data-tooltip-content={t('worksample.you_cannot_make_changes_work_sample')}
						>
							<Dropdown
								className="workSampleSelect me-auto"
								disabled={isPreparing}
								icon={getSelectIcon()}
								options={[
									{
										label: t('worksample.select_all_pages'),
										icon: <IconSelectMulti className={`icon-18 me-2 ${selectedAllPages && 'disabled'}`} />,
										disabled: selectedAllPages,
										onClick: () => selectAllPages()
									},
									{
										label: t('worksample.select_all_final_grade_work'),
										icon: <IconSelectBoxAll className="icon-18 me-2" />,
										disabled: false,
										onClick: () => selectGradedPages()
									},
									{
										label: t('worksample.deselect_all_pages'),
										icon: <IconDeselectMulti className={`icon-18 me-2 ${selectedPages && selectedPages.length === 0 && 'disabled'}`} />,
										disabled: selectedPages && selectedPages.length === 0,
										onClick: () => deSelectAllPages()
									}
								]}
							>
								{t('worksample.select')}
							</Dropdown>
						</span>
						<div className="ms-3 me-auto">
							{t('worksample.selected_pages', { count: selectedPages.length + (workSample.includeWhiteboard ? 1 : 0) + (includeExam && showExamAnswers ? 1 : 0) })}
						</div>
						<span
							data-tooltip-id={'tooltip-generate'}
							data-tooltip-content={t('worksample.select_pages_to_generate_work_sample')}
						>
							<Button
								onClick={methods.handleSubmit(handleGenerateWorkSample)}
								className="btn-secondary btn-md rounded-0 btn-generate-work-sample ms-auto"
								disabled={isPreparing || (selectedPages.length === 0 && includeExam === false && workSample.includeWhiteboard === false)}
								icon={
									!isPreparing ? <IconWorkSample className="icon-22" /> : <IconPreparing className="icon-22" />
								}>
								<div>
									{!isPreparing ? t('wip.generate_work_sample') : t('wip.preparing_work_sample')}</div>
							</Button>
						</span>
					</div>
				</div>
				<Container className="m-0 justify-content-center overflow-auto reader bg-light">
					<FormProvider {...methods}>
						<div className="d-flex flex-column flex-fill">
							<div className="reader">
								<Whiteboard editComments={true} isWorkSampleReview={true} isPreparing={isPreparing} />
								{showExamAnswers &&
									<div className="exam-container">
										<WorkSampleExam includeExam={includeExam} setIncludeExam={setIncludeExam} isWorkSampleReview={true} isPreparing={isPreparing} />
									</div>
								}
								<div className="document-container">
									<WorkSample isPrinting={true} selectedPages={selectedPages} setSelectedPages={setSelectedPages} isWorkSampleReview={true} isPreparing={isPreparing} />
								</div>
							</div>
						</div>
					</FormProvider>
				</Container>
			</div>
		</div >


	);
}

export default WorkSamplePage;